"use strict";
//import BooleanArray from "./pxene.BooleanArray";
const {floor} = Math;
/** mask a full rectangle **/
const MASK_16 = Math.pow(2, 16) - 1;
/** used to generate offset masks **/
const MASK_X = new Uint16Array(4);
/** used to generate offset masks **/
const MASK_Y = new Uint16Array(4);
/** mask offsets from the top **/
const MASK_TOP = new Uint16Array(4);
/** mask offsets from the bottom **/
const MASK_BOTTOM = new Uint16Array(4);
/** mask offsets from the right **/
const MASK_RIGHT = new Uint16Array(4);
/** mask offsets from the left **/
const MASK_LEFT = new Uint16Array(4);

/**
 * Precompute a bunch of useful masks for fast shape checks. Done this way
 * to illustrate/keep track of how they're constructed instead of just using
 * "magic numbers".
 */
MASK_X[0] = 1 | (1 << 4) | (1 << 8) | (1 << 12);
MASK_X[1] = MASK_X[0] << 1;
MASK_X[2] = MASK_X[0] << 2;
MASK_X[3] = MASK_X[0] << 3;
if((MASK_X[0] | MASK_X[1] | MASK_X[2] | MASK_X[3]) !== MASK_16) throw new Error("MASK_X is bad");

MASK_Y[0] = (1 << 4) - 1; 
MASK_Y[1] = MASK_Y[0] << 4;
MASK_Y[2] = MASK_Y[0] << 8;
MASK_Y[3] = MASK_Y[0] << 12;
if((MASK_Y[0] | MASK_Y[1] | MASK_Y[2] | MASK_Y[3]) !== MASK_16) throw new Error("MASK_Y is bad");

MASK_TOP[0] = 0;
MASK_TOP[1] = MASK_Y[3];
MASK_TOP[2] = MASK_TOP[1] | MASK_Y[2];
MASK_TOP[3] = MASK_TOP[2] | MASK_Y[1];

MASK_BOTTOM[0] = 0;
MASK_BOTTOM[1] = MASK_Y[0];
MASK_BOTTOM[2] = MASK_BOTTOM[1] | MASK_Y[1];
MASK_BOTTOM[3] = MASK_BOTTOM[2] | MASK_Y[2];

MASK_RIGHT[0] = 0;
MASK_RIGHT[1] = MASK_X[3];
MASK_RIGHT[2] = MASK_RIGHT[1] | MASK_X[2];
MASK_RIGHT[3] = MASK_RIGHT[2] | MASK_X[1];

MASK_LEFT[0] = 0;
MASK_LEFT[1] = MASK_X[0];
MASK_LEFT[2] = MASK_LEFT[1] | MASK_X[1];
MASK_LEFT[3] = MASK_LEFT[2] | MASK_X[2];


/**
 * @module pxene.CollisionMap
 *
 * Module containing {@link CollisionMap} prototype.
 */

const internal_array = Symbol();

/**
 * @constructor
 * A collision map is a 2d grid of boolean true/false values, meant to be
 * used for collision testing.
 *
 * @param {int} width the width of the grid
 * @param {int} height the height of the grid
 * @return {CollisionMap}
 */
export default function CollisionMap(width = 0, height = 0) {
	this.width = 0;
	this.height = 0;
	this.cellWidth = 0;
	this.cellHeight = 0;
	this.length = 0;
	if(width && height) this.init(width, height);
	return this;
}

/* helpful(?) constants */
/** pixel array index offset for the red channel **/
CollisionMap.CHANNEL_RED = 0;
/** pixel array index offset for the green channel **/
CollisionMap.CHANNEL_GREEN = 1;
/** pixel array index offset for the blue channel **/
CollisionMap.CHANNEL_BLUE = 2;
/** pixel array index offset for the alpha channel **/
CollisionMap.CHANNEL_ALPHA = 3;

/**
 * Creates a per-pixel collision map from a Canvas.
 * @param {Canvas} canvas the canvas to read pixel data from
 * @param {int} threshold the threshold above which a pixel will be considered solid (default 0) 
 * @param {int} channel the channel to check against (default {@link CollisionMap.CHANNEL_ALPHA}) 
 * @return {CollisionMap} 
 *
 * @note internal canvas pixel data stores alpha in a range of 0 to 255, so
 * convert from [0 - 1] to [0 - 255] if providing a threshold 
 */
CollisionMap.fromCanvasPixels = function(canvas, threshold = 0, channel = CollisionMap.CHANNEL_ALPHA) {
	console.time("new CollisionMap");
	let map = new CollisionMap(canvas.width, canvas.height);
	console.timeEnd("new CollisionMap");
	let pixels;
	let context = canvas.getContext("2d");
	console.time("fromCanvasPixels loop");
	let once = true;
	for(let y = 0, h = canvas.height; y < h; y += 100) {
		// go 100 rows at time with the image data for sanity/memory use
		try {
			if(once) console.time("getImageData");
			pixels = context.getImageData(0, y, canvas.width, 100).data;
			if(once) console.timeEnd("getImageData");
		}
		catch(e) {
			throw new Error("CollisionData:failed to get image data :(");
		}
		if(once) console.time("loop map.set");
		for(let i = 0, len = pixels.length; i < len; i+=4) {
			let mx = (i / 4) % canvas.width, my = y + (~~((i / 4) / canvas.width));
			if(pixels[i+channel] > threshold) map.set(mx, my, true);
		}
		if(once) console.timeEnd("loop map.set");
		once = false;
	}
	console.timeEnd("fromCanvasPixels loop");
	return map;
}

/**
 * Returns the bit for the given set of coordinates.
 */
const cellBit = CollisionMap.cellBit = function cellBit(x, y) {
	return 1 << ((x % 4) + ((y % 4)*4));
}

/**
 * Returns the mask which excludes the given bit coordinate from the cell.
 */
const cellMask = CollisionMap.cellMask = function cellMask(x, y) {
	return invertMask(cellBit(x, y));
}

const cellIndex = CollisionMap.cellIndex = function cellIndex(x, y, w) {
	return (floor(y/4) * floor(w/4)) + floor(x/4) 
}


/**
 * Reinitializes the map with a new width and height.
 * @param {int} width
 * @param {int} height
 * @return {self}
 */
CollisionMap.prototype.init = function(width, height) {
	this.width = ~~width;
	if(this.width % 4) this.width += (4 - (~~width % 4)); // round to nearest 4
	this.height = ~~height;
	if(this.height % 4) this.height += (4 - (~~height % 4)); // round to nearest 4
	this.cellWidth = this.width / 4;
	this.cellHeight = this.height / 4;
	let newlen = (this.cellWidth * this.cellHeight);
	if(this.length !== newlen) {
		this.length = newlen;
		if(this.length) {
			this[internal_array] = new Uint16Array(this.length);
		}
	}
	else if(this[internal_array]) this[internal_array].fill(0);
	return this;
}

CollisionMap.prototype.getCell = function getCell(x, y) {
	return this[internal_array][cellIndex(x, y, this.width)];
}

CollisionMap.prototype.get = function get(x, y) {
	return (this.getCell(x, y) & cellBit(x, y))?1:0;
}

CollisionMap.prototype.set = function set(x, y, v) {
	if(v) this[internal_array][cellIndex(x, y, this.width)] |= cellBit(x, y);
	else this[internal_array][cellIndex(x, y, this.width)] &= cellMask(x, y);
}

/**
 * Checks a rectangular area of the CollisionMap, returning a count of solid
 * grid sections within.
 *
 * @param {int} x start x coordinate
 * @param {int} y start y coordinate
 * @param {int} w width of rectangle
 * @param {int} h height of rectangle 
 * @return {int}
 */
CollisionMap.prototype.checkRect = function checkRect(x, y, w, h) {
	let offsetX = x % 4;
	let offsetY = y % 4;
	let cellWidth = Math.ceil((offsetX + w) / 4);
	let cellHeight = Math.ceil((offsetY + h) / 4);
	let maskX, maskY;
	for(let cellY = 0; cellY < cellHeight; ++cellY) {
		if(cellY === 0) maskY = MASK_TOP[offsetY];
		else if(cellY === (cellHeight - 1)) maskY = MASK_BOTTOM[offsetY];
		else maskY = MASK_16;
		for(let cellX = 0; cellX < cellWidth; ++cellX) {
			if(cellX === 0) maskX = MASK_LEFT[offsetX];
			else if(cellX === (cellWidth - 1)) maskX = MASK_RIGHT[offsetX];
			else maskX = MASK_16;
			if(maskX & maskY & this[internal_array][(cellY * this.cellWidth) + cellX]) return 1;
		}
	}

	return 0;
}

/**
 * Intersects two CollisionMaps, returning a sum of the count of overlapping
 * solid areas.
 * @param {CollisionMap} target the map to intersect with
 * @param {int} sx start x of this map
 * @param {int} sy start y of this map
 * @param {int} tx start x of the target map
 * @param {int} ty start y of the target map
 * @param {int} w width of area to collide
 * @param {int} h height of area to collide
 * @return {int}
 * @todo examine whether fast intersection by blocks of 8 sectors is doable
 */
CollisionMap.prototype.intersect = function intersect(target, sx, sy, tx, ty, w, h) {
	let x, y, sum = 0;
	for(y = 0; y < h; ++y) {
		for(x = 0; x < h; ++x) {
			sum += (this.get(sx+x, sy+y) && target.get(tx+x, ty+y))?1:0;	
		}
	}
	return sum;
}

function invertMask(mask) {
	return MASK_16 ^ mask;
}

// export constants for debugging
CollisionMap.MASK_X = MASK_X;
CollisionMap.MASK_Y = MASK_Y;
CollisionMap.MASK_TOP = MASK_TOP;
CollisionMap.MASK_BOTTOM = MASK_BOTTOM;
CollisionMap.MASK_LEFT = MASK_LEFT;
CollisionMap.MASK_RIGHT = MASK_RIGHT;



//////////////////
// WEBPACK FOOTER
// ../nphyx-pxene/src/pxene.CollisionMap.js
// module id = 37
// module chunks = 0